import { useEffect } from 'react';

import {
  SearchableVendorTaxonomyKey,
  SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getDeviceTypeState } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';

import { GetServerSideProps } from 'next';
import queryString from 'query-string';

import { getBookedVendorsAllowsMiniQuiz } from '~/components/common/modals/MiniQuizModal/hooks/useBookedVendorsAllowMiniQuiz';
import { findSlugsInPath } from '~/hooks/usePathSlugs';
import { getNavHtml } from '~/libs/server/api/nav';
import { fetchStorefrontSearchResults } from '~/libs/server/api/storefronts';
import { getSearchableFacets, getTaxonomyNodes } from '~/libs/server/api/taxonomy';
import { getUserContext } from '~/libs/server/api/user';
import { fetchVendorSearchResultsUserData } from '~/libs/server/api/vendorSearch';
import ApiService from '~/libs/server/util/apiService';
import { getSearchLocationForSlug } from '~/libs/universal/vendorMarket';
import { fetchVendorSearchResultsPageData } from '~/libs/universal/vendorSearch';
import { getMiniQuizUserSettingForTaxonomy } from '~/pages/couples/explore/components/MiniQuiz/miniQuizUserSettings';
import {
  SearchResultPageSearchResult,
  ShouldInsertMiniQuizByTaxonomy,
} from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { getVendorTaxonomiesByKey } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageProvider';
import { ControlPage } from '~/pages/couples/explore/components/SearchResults/ControlPage';
import { TestPage } from '~/pages/couples/explore/components/SearchResults/TestPage';
import { SearchResultsPageProps } from '~/pages/couples/explore/components/SearchResults/types/props';
import { getVendorCardGridLazyLoadLimit } from '~/pages/couples/explore/components/VendorCardGrid/VendorCardGrid';
import { buildBackendVendorSearchRequestFromSearchProperties } from '~/pages/couples/explore/util/buildSearchRequest';
import {
  locationSlugInPath,
  parseVendorSearchUrl,
} from '~/pages/couples/explore/util/parseVendorSearchUrl';
import { reduceTaxonomyWithFacetsToLookup } from '~/reducers/ui/couplesSearch';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { isAutomatedTest } from '~/util/automatedTestUtils';
import EXPERIMENT_FLAGS, { EXPERIMENT_FLAGS_SSR } from '~/util/experimentFlags';
import { ALL_MARKETS } from '~/util/searchUtils';
import { isVendorIdentified } from '~/util/vendorIdentification';

const Page = (props: SearchResultsPageProps) => {
  const { userContext, identifiedAsVendor, isInPreAuthSrpTest } = props;

  useEffect(() => {
    // Trigger experiment viewed
    if (!identifiedAsVendor) {
      EXPERIMENT_FLAGS.isUnderTest('marketplace_featured_vendors_holdout', userContext, true);
    }
  }, [identifiedAsVendor, userContext]);

  useEffectOnce(() => {
    // Trigger experiment viewed
    !isAutomatedTest() &&
      EXPERIMENT_FLAGS.isUnderTest('marketplace-search-results-header-test', userContext, true);
  });

  useEffectOnce(() => {
    if (userContext?.is_guest) {
      // Trigger experiment viewed for guests
      !isAutomatedTest() &&
        EXPERIMENT_FLAGS.isUnderTest('marketplace-srp-pre-auth-test', userContext, true);
    }
  });

  if (isInPreAuthSrpTest) {
    return <TestPage {...props} />;
  }

  return <ControlPage {...props} />;
};

const getLocationFromUrl = (slug: string | null): Promise<LocationOrAllMarketsType | null> => {
  const pathSlugs = findSlugsInPath(slug ?? '');
  const locationSlug = locationSlugInPath(pathSlugs);

  if (locationSlug) {
    return getSearchLocationForSlug(locationSlug, ApiService);
  }

  return Promise.resolve(ALL_MARKETS);
};

export const getServerSideProps: GetServerSideProps<SearchResultsPageProps> = async (context) => {
  const slug = Array.isArray(context.query.slug) ? context.query.slug[0] : context.query.slug ?? '';

  // Fetch the properties that are 100% static and have no dependencies.
  const [navHtml, userContext, taxonomyNodes, taxonomiesWithFacets, selectedLocation] =
    await Promise.all([
      getNavHtml(context.req),
      getUserContext(context.req),

      // taxonomyNodes: All the taxonomy nodes (venues/etc)
      getTaxonomyNodes().then((nodes) => {
        return [...nodes].sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          if (a.label > b.label) {
            return 1;
          }

          return 0;
        });
      }),

      // taxonomiesWithFacets: this drives the filters on the page
      getSearchableFacets(),

      // selectedLocation: The location parsed out of the slug and verified with the backend as valid
      getLocationFromUrl(slug),
    ]);

  // Transform the taxonomies (with their allowed facets) into a couple of static
  // lookups that let us easily find the facets for a given taxonomy or taxonomy/category
  const { facetsByVendorType, facetsByVendorTypeAndCategory } =
    reduceTaxonomyWithFacetsToLookup(taxonomiesWithFacets);

  // Transform the taxonomy nodes into a lookup so we can validate `wedding-venues` from the slug is allwoed
  const taxonomyNodesByKey = getVendorTaxonomiesByKey(taxonomyNodes);

  // Parse the URL out into its parts: the query string and the path
  const { query, url: pathname } = queryString.parseUrl(context.req.url || '', {
    arrayFormat: 'bracket',
  });

  // Parse the URL into a more usable format: the vendorSearch, the canonical url,
  // and all the selected facets.  This is the main driver of the page (the URL is
  // the source of truth for all the inputs to search).  This validates the slugs
  // in the URL and lets us know if anything are invalid (via the canonicalUrl)
  const parsedUrl = parseVendorSearchUrl({
    slug: slug,
    query: query,
    taxonomyNotesByKey: taxonomyNodesByKey,
    facetsByVendorType,
    selectedLocation,
  });

  // Here is where we find out of any of the parameters in the URL were invalid
  // and we redirect to the canonical URL if they were.
  if (parsedUrl.canonicalUrl && queryString.parseUrl(parsedUrl.canonicalUrl).url !== pathname) {
    return {
      redirect: {
        destination: parsedUrl.canonicalUrl,
        permanent: false,
      },
    };
  }

  // Now that we have the search (parsedUrl.vendorSearch) we can execute queries
  // that are related to search.  Some of these are static for the page and some
  // are specific to the user.
  const [
    [supplied, adjacentVendors, relatedLinks, realWeddings, expertAdvice, seoContent],
    [
      userSettings,
      accountVendors,
      vendorPreferences,
      weddingPreferences,
      showTopPicks,
      topPicks,
      recentlyViewedByTaxonomy,
      storefrontToInquiryMap,
      hasVenueBudget,
    ],
  ] = await Promise.all([
    // Static data for the page based on the city + taxonomy (and facets selected)
    fetchVendorSearchResultsPageData(parsedUrl.vendorSearch, facetsByVendorType, ApiService),

    // Static data for the page based on the _user_ (and the search)
    fetchVendorSearchResultsUserData(parsedUrl.vendorSearch, userContext, context.req),
  ]);

  // Determine if a vendor is viewing the page or if the couple is in the holdout
  // group.  For couples in the holdout group: they won't see featured vendors.
  // Since vendors have _paid for featured slots_ we want to always show them
  // featured vendors
  const identifiedAsVendor = isVendorIdentified((key: string) =>
    Boolean((context.req.cookies || {})[key])
  );

  const isInHoldoutGroupExperiment =
    EXPERIMENT_FLAGS_SSR.getExperimentVariation(
      'marketplace_featured_vendors_holdout',
      userContext,
      context.req.cookies || {}
    ) === 'TEST-1';

  const isInPreAuthSrpTest = Boolean(
    userContext?.is_guest &&
      EXPERIMENT_FLAGS_SSR.getExperimentVariation(
        'marketplace-srp-pre-auth-test',
        userContext,
        context.req.cookies || {}
      ) === 'TEST-1'
  );

  // Determine if the mini-quiz should be inserted (BY CATEGORY)
  // This is a static, one time lookup.  If the mini-quiz is answered
  // we need to hard reload the page to get a new lookup.
  const shouldInsertMiniQuiz: ShouldInsertMiniQuizByTaxonomy =
    SEARCHABLE_VENDOR_TAXONOMY_KEYS.reduce((result, taxonomyKey) => {
      const bookedVendorsAllowsQuiz = getBookedVendorsAllowsMiniQuiz(accountVendors, taxonomyKey);
      const userSettingKey = getMiniQuizUserSettingForTaxonomy(taxonomyKey);

      const userSetting = userSettingKey ? userSettings[userSettingKey] : undefined;

      const userSettingsAllowedQuiz = !(userSetting ? userSetting.flag : false);
      const shouldInsertQuizForCategory = bookedVendorsAllowsQuiz && userSettingsAllowedQuiz;

      return {
        ...result,
        [taxonomyKey]: shouldInsertQuizForCategory,
      };
    }, {});

  const initialCategory = parsedUrl.vendorSearch.selectedTaxonomy?.key;

  // Determine if the mini-quiz should be inserted on page load (for the current category)
  const shouldInsertMiniQuizThisCategory = Boolean(
    initialCategory && shouldInsertMiniQuiz[initialCategory as SearchableVendorTaxonomyKey]
  );

  // Determine if the BUDGET quiz should be inserted.  This is only for venues.
  // Like with the mini-quiz, this is a one-time static determination.
  const shouldInsertBudgetQuiz = initialCategory === VENUES_TAXONOMY_KEY && !hasVenueBudget;

  // Build and execute the search request.  This is finally running the vendor
  // search and getting us StorefrontCardViews we can render on the page.
  const vendorSearchRequest = buildBackendVendorSearchRequestFromSearchProperties({
    vendorSearch: parsedUrl.vendorSearch,
    shouldInsertBudgetQuiz,
    shouldInsertMiniQuiz: shouldInsertMiniQuizThisCategory,
    identifiedAsVendor,
    isInHoldoutGroupExperiment,
  });

  const searchResponse = await fetchStorefrontSearchResults(vendorSearchRequest, context.req);

  let vendorsAndLoad: Pick<SearchResultPageSearchResult, 'vendors' | 'deferredVendorLoad'> = {
    vendors: searchResponse.entities,
    deferredVendorLoad: null,
  };

  // Don't send all the cards and storefronts to the browser.  Send only what is
  // going to be visible on first paint.  This is a SEO performance optimization.
  // Originally, we would send up to 96 storefronts across the wire.  This includes
  // the JSON for the StorefrontCardView and the rendered HTML from next.  But, on
  // first paint, only a few cards are visible depending on the screen size.  So
  // instead of sending all the cards, we send the first N, and a deferred load flag
  // indicating to the page to load some additional cards async.
  //
  // In testing, this cut down the first HTML payload (gzipped) from 190k to around 145k
  const { isMobileDevice } = getDeviceTypeState(context.req.headers['user-agent']);
  const lazyLoadCardLimit = getVendorCardGridLazyLoadLimit(isMobileDevice);
  if (vendorsAndLoad.vendors.length > lazyLoadCardLimit) {
    const limit = (vendorSearchRequest?.limit || 0) - lazyLoadCardLimit;

    vendorsAndLoad = {
      vendors: searchResponse.entities.slice(0, lazyLoadCardLimit),
      deferredVendorLoad: {
        offset: lazyLoadCardLimit,
        limit: limit < 0 ? 0 : limit,
      },
    };
  }

  // Transform the search response into the form used on the page.
  const searchResults: SearchResultPageSearchResult = {
    totalHits: searchResponse.totalHits,
    offset: searchResponse.offset,
    shouldInsertMiniQuiz: shouldInsertMiniQuizThisCategory,
    shouldInsertBudgetQuiz,
    ...vendorsAndLoad,
  };

  return {
    props: {
      userContext,
      navHtml,
      parsedUrl,
      userAgent: context.req.headers['user-agent'],
      facetsByVendorType,
      facetsByVendorTypeAndCategory,
      seoContent,
      supplied,
      adjacentVendors,
      relatedLinks,
      realWeddings,
      expertAdvice,
      userSettings,
      accountVendors,
      vendorPreferences,
      weddingPreferences,
      showTopPicks,
      topPicks,
      identifiedAsVendor,
      isInHoldoutGroupExperiment,
      recentlyViewedByTaxonomy,
      storefrontToInquiryMap,
      hasVenueBudget,
      shouldInsertMiniQuiz,
      searchResults,
      isInPreAuthSrpTest,
    },
  };
};

export default Page;
